import { ChangeDetectionStrategy, Component, computed, effect, input, model, OnInit, output } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, filter } from 'rxjs/operators';

import { MessageCenterAuthService, MessageCenterService } from '@app/modules/message-center/services';
import { PaginatedResponse } from '@app/models/paginated-response.model';
import { ChatsLookupField, ConversationType, UserChat } from '@app/modules/message-center/models/chat.model';
import { GroupType } from '@app/core/enums/message-center/group-type.enum';
import { ItemsComponent, MergeStrategy, RealtimeEventHandler } from '@app/core/components';
import { WsConnectionModule, WsData, WsEvent } from '@app/core/services/websocket/ws.models';
import { getRemainingScrollDistanceToBottom } from '@app/common';
import { PaginationIfc } from '@app/shared/interfaces/pagination.class';
import { filterWsEvents } from '@app/core/services/websocket/rxjs-ws-operators';
import { Message } from '@app/modules/message-center/models/message.model';
import { DoIfConfirmed } from '@app/core/decorators';
import { CreateGroupComponent } from '../create-group/create-group.component';
import { FilterPill } from '@app/shared/components/filter-pills/filter-pills.component';
import { MCProfile } from '../models/user.model';

@Component({
  selector: 'app-chats-list',
  templateUrl: './chats-list.component.html',
  styleUrls: ['./chats-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChatsListComponent extends ItemsComponent<UserChat> implements OnInit {
  activeChatId = input<number>();
  navigateOnTabChange = input<boolean>(false);
  selectedTab = model<'messages' | 'sms'>('messages');

  openChat = output<UserChat>();

  filtrationForm: FormGroup;
  searchText: string;
  override pagination: PaginationIfc = new PaginationIfc(15);

  readonly unreadMessages = computed(() => this.messageCenterService.unreadMessages());
  readonly unreadSmsMessages = computed(() => this.messageCenterService.unreadSmsMessages());

  protected mcProfile: MCProfile;
  protected override responseMergeStrategy = MergeStrategy.Append;
  protected deleteItemRealtimeEvents = [WsEvent.deleteChat];
  protected updateItemRealtimeEvents = [WsEvent.chatListUpdating, WsEvent.chatUpdating];
  protected createItemRealtimeEvents: RealtimeEventHandler<UserChat, UserChat>[] = [
    { name: WsEvent.newChat, filter: (message: WsData<UserChat>) => this.chatCreatedForSelectedTab(message.payload) }
  ];
  protected override defaultEventMapper = (data: WsData<any>) => data.payload;

  readonly ConversationType = ConversationType;
  readonly filterPills: FilterPill<ChatsLookupField>[] = [
    { label: 'messageCenter.chatType_all', value: ChatsLookupField.All },
    { label: 'messageCenter.chatType_staff', value: ChatsLookupField.Staff },
    { label: 'messageCenter.chatType_caregiver', value: ChatsLookupField.Caregiver },
    { label: 'messageCenter.chatType_chats', value: ChatsLookupField.Chats }
  ];

  get isLoadingWithReplace(): boolean {
    return this.isLoading && this.responseMergeStrategy === MergeStrategy.Replace;
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private messageCenterService: MessageCenterService,
    private messageCenterAuthService: MessageCenterAuthService,
  ) {
    super();
    this.mcProfile = this.messageCenterAuthService.getProfile();
    this.realtimeModules = [
      { module: WsConnectionModule.listOfChats, user_id: this.mcProfile?.id }
    ];
  }

  ngOnInit() {
    this.filtrationForm = new FormGroup({
      search: new FormControl(''),
      lookup_field: new FormControl(this.filterPills[0].value),
      access_type: new FormControl(this.selectedTab() === 'sms' ? [GroupType.SMS] : [GroupType.Public, GroupType.Private])
    });

    super.ngOnInit();

    this.filtrationForm.valueChanges
      .pipe(debounceTime(500), untilDestroyed(this))
      .subscribe(() => {
        this.searchText = this.filtrationForm.controls.search.value;
        this.responseMergeStrategy = MergeStrategy.Replace;
        this.items = [];
        this.pagination.offsetChanged(0);
        this.loadItems();
      });
  }

  selectTab(tab: 'messages' | 'sms'): void {
    this.selectedTab.set(tab);
    const accessType = tab === 'sms'
      ? [GroupType.SMS]
      : [GroupType.Public, GroupType.Private];
    this.filtrationForm.patchValue({ access_type: accessType });

    // Navigate to not selected chat state
    if (this.navigateOnTabChange()) {
      this.router.navigate(['/', 'message-center']);
    }
  }

  protected override getItems(): Observable<PaginatedResponse<UserChat>> {
    return this.messageCenterService.getUsersChats({
      offset: this.items.length,
      limit: this.pagination.limit,
      ...this.filtrationForm.value,
    });
  }

  protected override subscribeToRealtime() {
    super.subscribeToRealtime();
    this.realtimeService.messages$
      .pipe(filterWsEvents<Message>([WsEvent.newMessage]), untilDestroyed(this))
      .subscribe((payload) => {
        const chat: UserChat = this.items.find(item => item.id === payload.chat);
        const isOwnMessage = payload.sender.id === this.mcProfile?.id;

        if (chat) {
          const alreadyHasMessage = chat.unread_messages_ids.some(id => id === payload.id);

          this.updateItem({
            id: payload.chat,
            last_message: !payload.reply_to ? { ...payload, has_files: !!payload.files.length } : chat.last_message,
            unread_messages_ids: (isOwnMessage || alreadyHasMessage) ? chat.unread_messages_ids : [...chat.unread_messages_ids, payload.id]
          });
        }
      });

    this.realtimeService.messages$
      .pipe(filterWsEvents<{ id: number, chat: number }>([WsEvent.userViewedMessage]), untilDestroyed(this))
      .subscribe((payload) => {
        const chat: UserChat = this.items.find(item => item.id === payload.chat);
        this.updateItem({
          id: payload.chat,
          unread_messages_ids: (chat?.unread_messages_ids ?? []).filter(id => id !== payload.id),
        });
      });
  }

  protected createItem(newItem: UserChat) {
    const chatForReplace = this.items.findIndex((item) => newItem.name === item.name && item.id == null);
    if (chatForReplace !== -1) {
      this.items[chatForReplace] = newItem;
      this.cdr.markForCheck();
    } else {
      super.createItem(newItem);
    }
  }

  onChatClick(chat: UserChat): void {
    if (chat.id == null) {
      this.messageCenterService.createDirectChat({ direct_to: chat.direct_to.id, direct_to_meta: chat.direct_to.meta })
        .subscribe({
          next: (response) => this.openChat.emit(response),
          error: (error) => this.notificationsService.showError(error),
        });
    } else {
      this.openChat.emit(chat);
    }
  }

  private chatCreatedForSelectedTab(chat: UserChat): boolean {
    return this.filtrationForm.controls.access_type.value.includes(chat.access_type);
  }

  createChat(): void {
    this.dialog.open(CreateGroupComponent, {
      width: '100%',
      maxWidth: '596px',
      maxHeight: '90vh',
      restoreFocus: false,
    })
    .afterClosed()
    .pipe(
      filter((res: UserChat) => !!res && this.chatCreatedForSelectedTab(res)),
      untilDestroyed(this)
    ).subscribe((res: UserChat) => this.openChat.emit(res));
  }

  @DoIfConfirmed({
    text: (translate: TranslateService, chat: UserChat) => translate.instant('messageCenter.deleteChatConfirmation', { name: chat.name })
  })
  deleteChat(chat: UserChat): void {
    this.messageCenterService.deleteUserChat(chat.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.deleteItem(chat),
        error: (error) => this.notificationsService.showError(error)
      });
  }

  onScroll(element: HTMLElement): void {
    const scroll = getRemainingScrollDistanceToBottom(element);

    if (scroll < 100 && !this.isLoadedAllItems && !this.isLoading) {
      this.pagination.offsetChanged(this.pagination.offset + this.pagination.limit);
      this.responseMergeStrategy = MergeStrategy.Append;
      this.loadItems();
    }
  }
}
